/**
 * ChatComposer — компоновщик исходящего сообщения админа.
 *
 *   - Rich-text editor (TipTap) с минимальным toolbar + AI-проверка (LanguageTool).
 *   - Множественные attachments: photo + video + audio + document — все вместе.
 *   - При клике «Отправить» → multipart POST к /admin/chat/dialogs/{user_id}/send
 *     с полями (html, files[], media_kinds JSON, captions JSON).
 *   - Backend разбивает один батч на N TG-сообщений: 1 текст + N медиа, всё
 *     в одной chat_message записи (один блок-массив).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Button, Space, Upload, message, Tag, Typography, Tooltip, Switch, Dropdown,
  type MenuProps,
} from "antd";
import {
  SendOutlined, FileImageOutlined, MobileOutlined,
  VideoCameraOutlined, SoundOutlined, FileOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import type { Editor } from "@tiptap/react";
import type { RcFile } from "antd/es/upload/interface";
import { RichTextEditor } from "./RichTextEditor";
import { MessagePreview, type AttachmentPreview } from "./MessagePreview";
import { API_URL } from "../dataProvider";

type MediaKind = "photo" | "video" | "audio" | "document" | "voice";

type PendingAttachment = AttachmentPreview;

interface Props {
  userId: number;
  onSent?: () => void;
  /** Юзер заблокировал бота — composer становится read-only + Alert. */
  botBlocked?: boolean;
}

const KIND_ICON: Record<MediaKind, string> = {
  photo: "📷", video: "🎥", audio: "🎵", voice: "🎙",  document: "📄",
};
const KIND_COLOR: Record<MediaKind, string> = {
  photo: "magenta", video: "purple", audio: "cyan", voice: "geekblue", document: "blue",
};

let _localUidCounter = 0;
const nextUid = () => `att-${Date.now()}-${++_localUidCounter}`;

// LocalStorage key — запоминаем preference юзера показывать ли preview
const LS_PREVIEW_KEY = "bemli-chat-preview-on";

// ─── Variables (быстрая вставка из dropdown) ────────────────────────────────
//
// API возвращает { values: {var: "preview"}, supported: [{name,group,label}] }.
// При клике на пункт меню мы вставляем "{var}" в позицию курсора TipTap-
// редактора, а backend при отправке резолвит plaholder в реальное значение
// для конкретного юзера. Это даёт админу live-preview прямо в выпадашке,
// а не статический список.
interface VarSpec {
  name:  string;
  group: string;
  label: string;
}
interface VariablesResp {
  values:    Record<string, string>;
  supported: VarSpec[];
}

export const ChatComposer = ({ userId, onSent, botBlocked = false }: Props) => {
  const [html, setHtml] = useState("");
  const [isEmpty, setIsEmpty] = useState(true);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [sending, setSending] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [previewOn, setPreviewOn] = useState<boolean>(() => {
    const stored = localStorage.getItem(LS_PREVIEW_KEY);
    return stored === null ? true : stored === "1";
  });
  const togglePreview = (v: boolean) => {
    setPreviewOn(v);
    localStorage.setItem(LS_PREVIEW_KEY, v ? "1" : "0");
  };

  // ─── Variables ──────────────────────────────────────────────────────────
  const [vars, setVars] = useState<VariablesResp | null>(null);
  const [varsLoading, setVarsLoading] = useState(false);
  const editorRef = useRef<Editor | null>(null);
  const onEditorReady = useCallback((e: Editor | null) => {
    editorRef.current = e;
  }, []);

  // Загружаем переменные при смене юзера. Тихо — даже если не загрузилось,
  // dropdown откроется (но значения будут "—" из локального fallback'а).
  useEffect(() => {
    if (!userId) {
      setVars(null);
      return;
    }
    let cancelled = false;
    setVarsLoading(true);
    fetch(`${API_URL}/admin/chat/dialogs/${userId}/variables`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: VariablesResp | null) => {
        if (cancelled) return;
        setVars(d);
      })
      .catch(() => { /* silent — dropdown всё равно будет работать */ })
      .finally(() => { if (!cancelled) setVarsLoading(false); });
    return () => { cancelled = true; };
  }, [userId, editorKey /* refetch после отправки — данные могут устареть */]);

  // Вставка `{var}` в текущую позицию курсора TipTap-редактора.
  // TipTap сам разруливает selection / focus / undo-history.
  const insertVariable = useCallback((name: string) => {
    const ed = editorRef.current;
    if (!ed) {
      message.warning("Редактор ещё не готов");
      return;
    }
    ed.chain().focus().insertContent(`{${name}}`).run();
  }, []);

  // Меню для Dropdown — группируем переменные по `group`.
  const varsMenu: MenuProps = useMemo(() => {
    const supported = vars?.supported ?? [];
    const values = vars?.values ?? {};
    // Сгруппируем по полю `group`, сохраняя порядок первого появления.
    const groups: { name: string; items: VarSpec[] }[] = [];
    for (const v of supported) {
      let g = groups.find((x) => x.name === v.group);
      if (!g) { g = { name: v.group, items: [] }; groups.push(g); }
      g.items.push(v);
    }
    if (groups.length === 0) {
      return { items: [{
        key: "_empty", label: "Нет доступных переменных", disabled: true,
      }] };
    }
    const items: MenuProps["items"] = [];
    groups.forEach((g, gi) => {
      if (gi > 0) items.push({ type: "divider" });
      items.push({
        key: `__group_${g.name}`,
        label: (
          <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {g.name}
          </Typography.Text>
        ),
        disabled: true,
      });
      for (const v of g.items) {
        const preview = values[v.name] ?? "—";
        const isEmpty = preview === "—";
        items.push({
          key: v.name,
          onClick: () => insertVariable(v.name),
          label: (
            <Tooltip
              placement="left"
              title={isEmpty
                ? `У этого юзера нет данных для {${v.name}} — при отправке заменится на «—»`
                : `Вставит \`{${v.name}}\`. При отправке заменится на: ${preview}`}
            >
              <div style={{
                display: "flex", justifyContent: "space-between",
                gap: 16, minWidth: 320, opacity: isEmpty ? 0.55 : 1,
              }}>
                <span>
                  <code style={{ fontSize: 12, background: "#f5f5f5",
                                 padding: "1px 6px", borderRadius: 3 }}>
                    {`{${v.name}}`}
                  </code>{" "}
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {v.label}
                  </Typography.Text>
                </span>
                <Typography.Text strong style={{
                  fontSize: 12, maxWidth: 200,
                  overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {preview}
                </Typography.Text>
              </div>
            </Tooltip>
          ),
        });
      }
    });
    return { items };
  }, [vars, insertVariable]);

  const addFile = (kind: MediaKind) => (file: RcFile) => {
    setAttachments((prev) => [...prev, { uid: nextUid(), file, kind }]);
    return false;
  };

  const removeAt = (uid: string) => {
    setAttachments((prev) => prev.filter((a) => a.uid !== uid));
  };

  const send = async () => {
    if (isEmpty && attachments.length === 0) {
      message.warning("Пустое сообщение");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      if (!isEmpty && html.trim()) {
        const cleaned = html.replace(/^<p><\/p>$/, "").trim();
        if (cleaned) fd.append("html", cleaned);
      }
      attachments.forEach((a) => fd.append("files", a.file, a.file.name));
      if (attachments.length > 0) {
        fd.append("media_kinds", JSON.stringify(attachments.map((a) => a.kind)));
      }
      const r = await fetch(`${API_URL}/admin/chat/dialogs/${userId}/send`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!r.ok) {
        const txt = await r.text().catch(() => r.statusText);
        message.error(`Ошибка ${r.status}: ${txt.slice(0, 300)}`);
        return;
      }
      setHtml(""); setIsEmpty(true);
      setAttachments([]);
      setEditorKey((k) => k + 1);
      onSent?.();
    } catch (e) {
      message.error(`Сетевая ошибка: ${(e as Error).message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ padding: 12, borderTop: "1px solid #e8e8e8", background: "#fafafa",
                  display: "flex", flexDirection: "column",
                  flex: "1 1 0", minHeight: 380 }}>
      {/* Bot-blocked banner — юзер заблокировал бота, отправка невозможна */}
      {botBlocked && (
        <Alert
          type="error"
          showIcon
          message="🚫 Юзер заблокировал бота"
          description="Отправка сообщений недоступна — TG возвращает 403. История переписки сохранена для чтения. Когда юзер разблокирует бота — composer станет активным автоматически."
          style={{ marginBottom: 10 }}
        />
      )}
      {/* TOP BAR — preview toggle справа */}
      <div style={{ display: "flex", justifyContent: "flex-end",
                    marginBottom: 6, alignItems: "center", gap: 8 }}>
        <Tooltip title="Превью того, как сообщение увидит клиент на телефоне">
          <Space size={4}>
            <MobileOutlined style={{ color: previewOn ? "#1677ff" : "#aaa" }} />
            <Typography.Text style={{ fontSize: 12, color: previewOn ? "#1677ff" : "#888" }}>
              Превью
            </Typography.Text>
            <Switch size="small" checked={previewOn} onChange={togglePreview} />
          </Space>
        </Tooltip>
      </div>

      {/* MIDDLE — split: editor + preview */}
      <div style={{ flex: "1 1 0", minHeight: 0, display: "flex",
                    gap: 12, overflow: "hidden" }}>
        <div style={{ flex: 1, minWidth: 0, display: "flex",
                      flexDirection: "column" }}>
          <RichTextEditor
            key={editorKey}
            initialHtml=""
            placeholder={
              botBlocked
                ? "Юзер заблокировал бота — отправка недоступна"
                : "Сообщение клиенту. Кнопка «🔧 Переменная» — вставит {end_at}/{tariff}/{sub_url} и др. ✨ — AI-проверка."
            }
            onChange={(h, empty) => { setHtml(h); setIsEmpty(empty); }}
            minHeight={240}
            enableProofread
            disabled={botBlocked}
            onEditorReady={onEditorReady}
          />
        </div>
        {previewOn && (
          <div style={{ flex: "0 0 340px",
                        // height: 100% — даёт MessagePreview правильно
                        // вычислить высоту scroll-area. Без него flex
                        // child может расширяться до intrinsic-size контента.
                        height: "100%", minHeight: 0,
                        display: "flex", flexDirection: "column" }}>
            <MessagePreview html={html} attachments={attachments} />
          </div>
        )}
      </div>

      {attachments.length > 0 && (
        <div style={{ marginTop: 8, padding: "8px 10px", background: "#fff",
                      border: "1px solid #d9d9d9", borderRadius: 4 }}>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            Прикреплено ({attachments.length}):
          </Typography.Text>
          <div style={{ marginTop: 4 }}>
            <Space size={[4, 4]} wrap>
              {attachments.map((a) => (
                <Tag key={a.uid} color={KIND_COLOR[a.kind]} closable
                     onClose={(e) => { e.preventDefault(); removeAt(a.uid); }}
                     style={{ fontSize: 12, padding: "2px 8px" }}>
                  {KIND_ICON[a.kind]} <strong>{a.file.name}</strong>{" "}
                  <span style={{ opacity: 0.7 }}>
                    ({(a.file.size / 1024).toFixed(0)} KB)
                  </span>
                </Tag>
              ))}
            </Space>
          </div>
        </div>
      )}

      <Space style={{ marginTop: 10, width: "100%", justifyContent: "space-between" }}>
        <Space size={6} wrap>
          <Tooltip title="Можно прикрепить несколько файлов сразу — фото, видео, аудио и документ одновременно">
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>📎</Typography.Text>
          </Tooltip>
          <Upload beforeUpload={addFile("photo")} showUploadList={false} accept="image/*" multiple disabled={botBlocked}>
            <Button icon={<FileImageOutlined />} disabled={botBlocked}>Фото</Button>
          </Upload>
          <Upload beforeUpload={addFile("video")} showUploadList={false} accept="video/*" multiple disabled={botBlocked}>
            <Button icon={<VideoCameraOutlined />} disabled={botBlocked}>Видео</Button>
          </Upload>
          <Upload beforeUpload={addFile("audio")} showUploadList={false} accept="audio/*" multiple disabled={botBlocked}>
            <Button icon={<SoundOutlined />} disabled={botBlocked}>Аудио</Button>
          </Upload>
          <Upload beforeUpload={addFile("document")} showUploadList={false} multiple disabled={botBlocked}>
            <Button icon={<FileOutlined />} disabled={botBlocked}>Файл</Button>
          </Upload>
          <Tooltip title="Вставить переменную: {end_at}, {tariff}, {sub_url} и др. — при отправке подставится реальное значение этого юзера">
            <Dropdown menu={varsMenu} trigger={["click"]} placement="topLeft"
                      overlayStyle={{ maxHeight: 480, overflowY: "auto" }}
                      disabled={!userId || botBlocked}>
              <Button icon={<TagsOutlined />} loading={varsLoading} disabled={botBlocked}>
                Переменная
              </Button>
            </Dropdown>
          </Tooltip>
        </Space>
        <Tooltip title={botBlocked ? "Юзер заблокировал бота — отправка недоступна" : ""}>
          <Button type="primary" size="large" icon={<SendOutlined />} loading={sending}
                  onClick={send}
                  disabled={botBlocked || (isEmpty && attachments.length === 0)}>
            Отправить
          </Button>
        </Tooltip>
      </Space>
    </div>
  );
};
